import React, { useEffect, useState } from "react";
import "./Dashboard.css";
import { Skeleton } from "@mui/material";
import Topbar from "../Global/topbar";
import CachedIcon from "@mui/icons-material/Cached";
import { getUsers } from "../../services/api/users";
import { getRechages } from "../../services/api/recharges";

/**
 * Admin landing. The old dashboard called analytics/recent_transactions and
 * analytics/overall — neither route exists on the Premika backend, so every
 * card silently showed ₹0 and the report stayed empty. There is no
 * platform-wide transaction feed exposed to the admin, so this is rebuilt on
 * the data that genuinely exists: total users and the subscription-plan
 * catalogue. Numbers are real or an explicit error — never a misleading zero.
 */
const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [userCount, setUserCount] = useState(null);
  const [userApprox, setUserApprox] = useState(false);
  const [planCount, setPlanCount] = useState(null);

  const loadStats = async () => {
    setLoading(true);
    setError(false);
    try {
      const [usersRes, plansRes] = await Promise.all([
        getUsers("", 1000, null, "", ""),
        getRechages("", 200, null),
      ]);

      // The backend has no cheap total-count (DynamoDB can't count without a
      // scan, and the old analytics aggregate is gone). Count what a single
      // large page returns and flag it as a lower bound ("+") if the result
      // was truncated (LastEvaluatedKey present).
      const ud = usersRes?.data?.data;
      const uTotal = ud?.total;
      if (typeof uTotal === "number") {
        setUserCount(uTotal);
        setUserApprox(false);
      } else {
        const arr = Array.isArray(ud?.data) ? ud.data : [];
        setUserCount(arr.length);
        setUserApprox(!!ud?.LastEvaluatedKey);
      }

      const plans = plansRes?.data?.data?.data;
      const pTotal = plansRes?.data?.data?.total;
      setPlanCount(
        typeof pTotal === "number"
          ? pTotal
          : Array.isArray(plans)
            ? plans.length
            : null,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("Dashboard stats failed:", e?.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const formatCount = (n) => {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString("en-IN");
  };

  const cards = [
    {
      icon: "👥",
      title: "Total Users",
      value: userCount,
      suffix: userApprox ? "+" : "",
    },
    { icon: "💳", title: "Subscription Plans", value: planCount, suffix: "" },
  ];

  return (
    <>
      <Topbar />
      <div className="dashboard">
        <header className="header">
          <h1 className="logo">Dashboard</h1>
          <div className="header-right">
            <button
              className="icon-btn"
              onClick={loadStats}
              title="Refresh"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
              }}
            >
              <CachedIcon /> Refresh
            </button>
          </div>
        </header>

        {error ? (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "#b3261e",
            }}
          >
            <p>Couldn’t load dashboard stats.</p>
            <button onClick={loadStats} style={{ cursor: "pointer" }}>
              Retry
            </button>
          </div>
        ) : (
          <div className="stats">
            {cards.map((card) => (
              <div
                key={card.title}
                className="card"
                style={{ borderTop: "4px solid var(--admin-page--color)" }}
              >
                <div className="icon">{card.icon}</div>
                <div className="sub-box">
                  <h2>{card.title}</h2>
                  <p>
                    {loading ? (
                      <Skeleton animation="wave" style={{ width: "60px" }} />
                    ) : (
                      `${formatCount(card.value)}${
                        card.value != null ? card.suffix : ""
                      }`
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboard;
